<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { onDestroy, onMount } from 'svelte';
	import Link from 'svelte-navaid/Link.svelte';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import Polling from '@/Components/Polling.svelte';
	// Partials
	import MeetingOverview from '@/Partials/Racing/Meeting/MeetingOverview.svelte';
	import Results from '@/Partials/Racing/Results/Results.svelte';
	import NextToJump from '@/Partials/Racing/Race/NextToJump.svelte';
	import RaceSelector from '@/Partials/Racing/Race/RaceSelector.svelte';
	import RaceDetails from '@/Partials/Racing/Race/RaceDetails.svelte';
	import SelectionsList from '@/Partials/Racing/Selections/SelectionsList.svelte';
	// Stores
	import MeetingStore from '@/Stores/Racing/MeetingStore.js';
	import RaceStore from '@/Stores/Racing/RaceStore.js';
	import Sockets from '@/Services/Sockets.js';
	import Helmet from '@/Components/Helmet.svelte';

	export let meeting_id = undefined;
	export let race_id = undefined;

	$: selectedMeetingId = meeting_id;
	$: selectedRaceId = race_id;
	$: selectedRaceId && selectedMeetingId && getRaceWithDetails(true);
	$: selectedMeetingId && changeMeeting();

	let daySelected;
	let loading = true;

	$: daySelected && loadNewDay();

	if (meeting_id) {
		MeetingStore.getSingleMeeting(meeting_id)
			.then((meeting) => {
				daySelected = dayjs(meeting.start_date).format('YYYY-MM-DD');
			})
			.catch(() => (daySelected = dayjs().format('YYYY-MM-DD')));
	} else {
		daySelected = dayjs().format('YYYY-MM-DD');
	}

	async function loadNewDay() {
		loading = true;
		await getMeetings(true, true).finally(() => (loading = false));

		if (!selectedMeetingId && !selectedRaceId) {
			getNextToJump();
		}
	}

	function getMeetings(reset = false, includeRaces = false) {
		return MeetingStore.getMeetings(daySelected, daySelected, reset, includeRaces);
	}

	function getRaceWithDetails(reset = false) {
		return RaceStore.getRaceWithDetails(selectedRaceId, selectedMeetingId, reset);
	}

	function getNextToJump() {
		selectedMeetingId = MeetingStore.getNextMeetingId();
		selectedRaceId = RaceStore.getNextRaceId(selectedMeetingId);
	}

	function changeMeeting() {
		loading = true;
		MeetingStore.getMeetingWithRaces(selectedMeetingId)
			.then((meeting) => {
				loading = false;
				daySelected = dayjs(meeting.start_date).format('YYYY-MM-DD');
			})
			.catch(() => {
				loading = false;
				daySelected = dayjs().format('YYYY-MM-DD');
			});
	}

	onMount(() => {
		if (meeting_id && race_id) {
			Sockets.subscribeToRacing();
		}
	});

	onDestroy(() => {
		if (meeting_id && race_id) {
			Sockets.unsubscribeFromRacing();
		}
	});
</script>

<Helmet title="Racing" />

<Polling pollFunction={() => getMeetings(false, true)} frequency={60} />
<Polling pollFunction={getRaceWithDetails} frequency={60} />

{#if loading}
	<Loading />
{:else if selectedMeetingId && !selectedRaceId}
	<div class="p-2 mx-2 bg-white rounded">
		<div class="flex justify-between">
			<div class="flex flex-1">
				<MeetingOverview bind:selectedMeetingId bind:selectedRaceId bind:daySelected />
			</div>
		</div>
	</div>
	<div class="py-10 text-center">
		<div>No races for this day</div>
		<div class="mt-4">
			<Link href="/racing/meetings" class="btn btn-primary">Load Next To Jump</Link>
		</div>
	</div>
{:else if selectedMeetingId && selectedMeetingId}
	<div class="p-2 mx-2 bg-white rounded">
		<div class="flex justify-between">
			<div class="flex flex-1">
				<MeetingOverview bind:selectedMeetingId bind:selectedRaceId bind:daySelected />
			</div>
			<div class="flex flex-1 mx-3">
				<Results {selectedRaceId} />
			</div>
			<div class="flex flex-1">
				<NextToJump {selectedMeetingId} />
			</div>
		</div>
		<RaceSelector {selectedRaceId} {selectedMeetingId} />
		<RaceDetails {selectedMeetingId} {selectedRaceId} />
		<SelectionsList {selectedRaceId} on:refresh_race={getRaceWithDetails} />
	</div>
{:else}
	<div class="py-10 text-center">
		<div>No meetings/races for this day</div>
		<div class="mt-4">
			<Link href="/racing/meetings" class="btn btn-primary">Load Next To Jump</Link>
		</div>
	</div>
{/if}
